import { useTranslation } from "@ferrivia/i18n";

/* The lab tests the Store tab is allowed to sell alongside the iron panel.
 *
 * A test reaches a client only if it appears BOTH here and in the catalog
 * mirror with an active price. That is two locks on purpose. The catalog is
 * whatever is switched on in Stripe, which is an operations decision; this
 * table is reviewed source, which is where copy about a laboratory test has
 * to live (see the comment on `catalogProductSchema` in @ferrivia/api-contract
 * for why the wire format carries no product copy at all). Adding a test to
 * the store is therefore a product change and a code change, not a click.
 *
 * **Nothing here says what a test is for.** Each row carries the test's name
 * and the laboratory's own code for it, and stops. A line like "choose this
 * if you are tired" would be a statement of intended purpose, and under MDR
 * Art. 2(12) promotional statements are constitutive of intended purpose — the
 * name on the row can qualify the app as a device with no algorithm anywhere
 * near it. The same rule is why a group is named after the part of the body a
 * measurement comes from rather than after a condition, and why the panel a
 * client sees is the same panel for every client: nothing on this page is
 * selected, ordered, preselected or priced from anything they have measured
 * (root CLAUDE.md, "The software does not interpret"; REQ-MDR-002, -004).
 *
 * A client who does not know what to choose gets a named human — the chat CTA
 * at the foot of the page — which is the only answer to that question we are
 * permitted to give.
 *
 * i18next-parser needs a literal key and an inline default per t() call, so
 * the rows are written out rather than looped from a data table — the same
 * reason productCopy.ts is written that way.
 */

/** Group a test is listed under, in the order the groups are rendered. */
export type LabAddOnGroupId = "blood";

export interface LabAddOn {
  /** Catalog slug — the join key to a price, exactly as elsewhere in the store. */
  slug: string;
  group: LabAddOnGroupId;
  name: string;
  /** The laboratory's own code for the measurement, e.g. "B-PVKT". Shown as
      the row's second line: it is what appears on the requisition and on every
      other Finnish lab's price list, and it is an identifier rather than a
      claim. */
  code: string;
}

export interface LabAddOnGroup {
  id: LabAddOnGroupId;
  label: string;
  addOns: LabAddOn[];
}

/**
 * The add-ons, grouped, in a fixed order. Identical for every client.
 *
 * One group and one test today, which is what the catalog actually carries
 * (`pvk-lisamittaus`). The shape is the point: a second group appears here the
 * day a vitamin D or thyroid product exists in Stripe, and nothing else in the
 * lab page changes.
 */
export function useLabAddOnGroups(): LabAddOnGroup[] {
  const { t } = useTranslation("client");

  const pvk: LabAddOn = {
    slug: "pvk-lisamittaus",
    group: "blood",
    // The same name the Results tab gives this panel when the values come
    // back (@ferrivia/lab-ui's PvkAccordion) — a client should not have to
    // work out that the thing they bought and the thing they are reading are
    // the same panel.
    name: t("store.lab.tests.pvk.name", "Perusverenkuva (PVK)"),
    code: t("store.lab.tests.pvk.code", "B-PVKT"),
  };

  const groups: LabAddOnGroup[] = [
    {
      id: "blood",
      label: t("store.lab.groups.blood", "Rauta ja verenkuva"),
      addOns: [pvk],
    },
  ];

  return groups;
}
